import { Id, Pagination } from '@eagle/api-types';
import { Geofence } from '@eagle/core-data-types';
import { Divider, ListItemText, Paper, Stack, TablePagination, Typography, styled } from '@ethings/ui-toolkit';
import { Box, unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { Position } from '@turf/turf';
import L from 'leaflet';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';
import { API_CALL_TEXT_LENGTH, SEARCH_RESULT_THING_LIMIT, T_MANY } from '../../constants';
import { useFetchAllCache, useMapContext, usePromise, useResponsiveText } from '../../hooks';
import { FindItemsResult } from '../../pages';
import { useSearch } from '../../pages/list/use-search';
import { scrollbar } from '../../style';
import { CacheDataTypes, CommonEntityWithDeleted, ToggleLayers } from '../../types';
import { filterDeleted, innerText, trackEvent } from '../../util';
import { testid } from '../../util/test-id';
import { FilterTypes } from '../filter';
import { useBoolFlag } from '../flags';
import { Icon, PageIcon } from '../icons';
import { LocationPin, useMapLayers } from '../map';
import { getFilterProps } from '../map/layer-selection/sub-menu/sub-menu.util';
import { coordinatesToLatLng } from '../map/layers/route-matching/util';
import { MapDiscoverItem } from '../search-thing-map';
import { SearchResultsProps } from './types';

interface StyledTablePaginationProps {
  isInlayMap: boolean | undefined;
}

const StyledPaper = styled(Paper)({
  cursor: 'pointer',
  flex: [1, 1],
});

const StyledTablePagination = styled(TablePagination)<StyledTablePaginationProps>(({ theme, isInlayMap }) => ({
  border: 'none',
  overflowX: 'hidden',
  '& .MuiTablePagination-actions': { marginLeft: isInlayMap ? 0 : theme.spacing(2.5) },
  '& .MuiToolbar-root': { px: isInlayMap ? 0 : theme.spacing(0.25) },
}));

const ScrollableResultsStack = styled(Stack)<{ isLoading: boolean }>(({ isLoading, theme }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
  ...(unstableStyleFunctionSx({ sx: scrollbar, theme } as never) as Record<string, unknown>),
}));

/**
  * FUNCTION: Display an search result Item
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function SearchMapResults<T extends Id<string>, U, O>({
  displayOnlyLocations = false,
  handleDrawerClose,
  handleFormatAddressItem = () => <></>,
  handleFormatGeofenceItem = () => <></>,
  handleFormatListItem = () => <></>,
  highlightIndex,
  isInlayMap,
  isLoading,
  noResultsInstructions,
  onAddressClick,
  searchQuery,
  searchResults,
}: SearchResultsProps<T, U, O>): JSX.Element {
  const map = useMap();
  const { onAddressSelected, onGeofenceSelected } = useMapContext();
  const { t } = useTranslation(['common', 'terms']);
  const { geofenceFilters, setGeofenceFilters, selectedLayers, setSelectedLayers } = useMapLayers();

  const thingText = t('terms:thing', { count: T_MANY });
  const geofenceText = t('common:terms.geofence_other');
  const locationText = t('common:terms.location');

  const h6Styles = useResponsiveText('1.25rem', Math.max(innerText(thingText).length, innerText(geofenceText).length, innerText(locationText).length), {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });
  const { pagination, setPagination, geofencePagination, setGeofencePagination } = useSearch();
  const [searchPosition, setSearchPosition] = useState<L.LatLng>();
  const thingsLength = searchResults.things?.result.results.length ? Math.min(searchResults.things?.result.results.length, SEARCH_RESULT_THING_LIMIT) : 0;
  const geofenceLength = searchResults.geofences?.result.results.length ? Math.min(searchResults.geofences?.result.results.length, SEARCH_RESULT_THING_LIMIT) : 0;
  const showGeofenceSearch = useBoolFlag('portals-geofence-feature');
  const mapSearchEnhancementFlag = useBoolFlag('track-map-search-enhancements-temporary-20250213');
  const filterCache = useFetchAllCache(CacheDataTypes.GEOFENCE_TYPE);
  const [filterItems] = usePromise(
    async () => {
      const data = await filterCache.all<CommonEntityWithDeleted>();
      return filterDeleted(data);
    },
    [filterCache],
  );

  const bottomText = (searchResult: FindItemsResult<T | O>, paginationValue: Pagination): string => {
    if (searchResult && searchResult.result.itemCount > 0) {
      return t('common:component.search.hint.pagination-of', { startCount: paginationValue.skip + 1, endCount: Math.min(paginationValue.skip + 4, searchResult.result.itemCount), totalCount: searchResult.result.itemCount });
    }
    return (t('common:common.hint.list.no-results'));
  };

  const geofenceClick = (item: O): void => {
    const geofence = item as unknown as Geofence;
    const geofenceLayerToggled = selectedLayers.some((layer) => layer === ToggleLayers.geofences);
    if (!geofenceLayerToggled) setSelectedLayers([...selectedLayers, ToggleLayers.geofences]);
    const geofenceCoords = geofence.geometry.coordinates[0].map((itemCoords) => {
      return coordinatesToLatLng(itemCoords as Position);
    });
    if (!geofenceFilters.find((filter) => filter.id === geofence.geofenceTypeId)) {
      const geofenceItem = filterItems?.find((filterItem) => filterItem._id === geofence.geofenceTypeId);
      if (geofenceItem) {
        setGeofenceFilters([...geofenceFilters, getFilterProps(geofenceItem, ToggleLayers.geofences, FilterTypes.GEOFENCE_TYPE)]);
      }
    }
    onGeofenceSelected(geofenceCoords, map);
  };

  return <>
    {!displayOnlyLocations
      && <>
        <Box data-testid='thing-map-search-things' display="flex" flexDirection="column" alignItems="flex-start" px={2} py={1} width="100%">
          <Stack mt={1} direction="row" alignItems="center" spacing={1} >
            {mapSearchEnhancementFlag && <PageIcon icon={<Icon icon="ThingIcon" />} size='1.25rem' />}
            <Typography
              variant="h6"
              style={{
                fontSize: h6Styles.fontSize,
                lineHeight: h6Styles.lineHeight,
                wordBreak: h6Styles.wordBreak,
                whiteSpace: h6Styles.whiteSpace,
                overflow: h6Styles.overflow,
                textOverflow: h6Styles.textOverflow,
              }}
            >
              {thingText}
            </Typography>
          </Stack>
          <Stack width={1} style={{ filter: isLoading ? 'blur(1px)' : '', opacity: isLoading ? 0.66 : 1 }} data-testid="things-map-search-results">
            {!searchResults || !searchResults.things || searchResults.things.result.results.length === 0
              ? searchQuery && searchQuery.length < API_CALL_TEXT_LENGTH
                ? <Typography color="text.secondary" >{t('common:component.search.hint.less-than-count-map', { count: API_CALL_TEXT_LENGTH })}</Typography>
                : <Typography
                  color="text.secondary"
                  {...(mapSearchEnhancementFlag
                    ? {
                      variant: 'caption',
                      fontStyle: 'italic',
                    }
                    : { p: 1 })}
                >
                  {noResultsInstructions}
                </Typography>
              : searchResults.things.result.results.slice(0, SEARCH_RESULT_THING_LIMIT).map((thing, index) =>
                <Paper
                  key={index}
                  elevation={index === highlightIndex ? 3 : 0}
                >
                  {handleFormatListItem(thing, handleDrawerClose, searchQuery, index)}
                </Paper>,
              )}
          </Stack>
        </Box>

        {searchQuery && searchResults.things && searchResults.things.result.itemCount > SEARCH_RESULT_THING_LIMIT
          && <Stack direction='row' mt="auto" alignItems="center" px={2.5} data-testid="thing-map-search-pagination">
            <ListItemText secondary={bottomText(searchResults.things, pagination)} />
            <StyledTablePagination
              component='div'
              count={searchResults.things.result.itemCount}
              labelDisplayedRows={() => ('')}
              labelRowsPerPage={''}
              onPageChange={(_: unknown, page: number) => {
                setPagination({ limit: SEARCH_RESULT_THING_LIMIT, skip: SEARCH_RESULT_THING_LIMIT * (page) });
                trackEvent('list', 'update_page', 'thing_map', { 'results_type': 'thing', 'page_number': page });
              }}
              page={Math.floor(pagination.skip / SEARCH_RESULT_THING_LIMIT)}
              rowsPerPage={SEARCH_RESULT_THING_LIMIT}
              slotProps={{ select: { style: { display: 'none' } } }}
              isInlayMap={isInlayMap}
            />
          </Stack>
        }
        {showGeofenceSearch && <>
          <Box my={0.5} mx={2}>
            <Divider />
          </Box>
          <Stack
            data-testid="thing-map-search-geofences"
            alignItems="flex-start" display="flex" flexDirection="column" p={2} pt={1} width="100%"
            spacing={1}
          >
            <Stack direction="row" alignItems="center" spacing={1} >
              {mapSearchEnhancementFlag && <PageIcon icon={<Icon icon="GeofenceIcon" />} size='1.25rem' />}
              <Typography
                variant="h6"
                style={{
                  fontSize: h6Styles.fontSize,
                  lineHeight: h6Styles.lineHeight,
                  wordBreak: h6Styles.wordBreak,
                  whiteSpace: h6Styles.whiteSpace,
                  overflow: h6Styles.overflow,
                  textOverflow: h6Styles.textOverflow,
                }}
              >
                {geofenceText}
              </Typography>
            </Stack>
            <Stack spacing={1.5} width={1} style={{ filter: isLoading ? 'blur(1px)' : '', opacity: isLoading ? 0.66 : 1 }}>
              {!searchResults.geofences || searchResults.geofences?.result.results.length === 0
                ? searchQuery && searchQuery.length < API_CALL_TEXT_LENGTH
                  ? <Typography noWrap color="text.secondary" p={1}>{t('common:component.search.hint.less-than-count-map', { count: API_CALL_TEXT_LENGTH })}</Typography>
                  : <Typography
                    color="text.secondary"
                    noWrap
                    {...(mapSearchEnhancementFlag
                      ? {
                        variant: 'caption',
                        fontStyle: 'italic',
                      }
                      : { p: 1 }
                    )}
                  >
                    {noResultsInstructions}
                  </Typography>
                : searchResults.geofences.result.results.slice(0, SEARCH_RESULT_THING_LIMIT).map((item, index) => (
                  <StyledPaper
                    key={index}
                    data-testid={testid`thing-map-search-geofence-${index}`}
                    elevation={(index + thingsLength) === highlightIndex ? 3 : 0}
                    onClick={() => geofenceClick(item)}
                  >
                    {handleFormatGeofenceItem(item, searchQuery)}
                  </StyledPaper>
                ))
              }
            </Stack>
          </Stack>

          {searchQuery && searchResults.geofences && searchResults.geofences.result.itemCount > SEARCH_RESULT_THING_LIMIT
            && <Stack direction="row" mt="auto" alignItems="center" px={2.5} data-testid="thing-map-search-pagination">
              <ListItemText secondary={bottomText(searchResults.geofences, geofencePagination)} />
              <StyledTablePagination
                component="div"
                count={searchResults.geofences.result.itemCount}
                labelDisplayedRows={() => ('')}
                labelRowsPerPage={''}
                onPageChange={(_: unknown, page: number) => {
                  setGeofencePagination({ limit: SEARCH_RESULT_THING_LIMIT, skip: SEARCH_RESULT_THING_LIMIT * (page) });
                  trackEvent('list', 'update_page', 'thing_map', { 'results_type': 'geofence', 'page_number': page });
                }}
                page={Math.floor(geofencePagination.skip / SEARCH_RESULT_THING_LIMIT)}
                rowsPerPage={SEARCH_RESULT_THING_LIMIT}
                slotProps={{ select: { style: { display: 'none' } } }}
                isInlayMap={isInlayMap}
              />
            </Stack>
          }
        </>}
        <Box my={0.5} mx={2}>
          <Divider />
        </Box>
      </>
    }
    <Stack spacing={1} flexDirection="column" alignItems="flex-start" p={displayOnlyLocations ? 0 : 2} pt={1} width="100%" data-testid="thing-map-search-locations">
      <Stack direction="row" alignItems="center" spacing={1} >
        {mapSearchEnhancementFlag && <PageIcon icon={<Icon icon="LocationIcon" />} size='1.25rem' />}
        <Typography
          variant="h6"
          style={{
            fontSize: h6Styles.fontSize,
            lineHeight: h6Styles.lineHeight,
            wordBreak: h6Styles.wordBreak,
            whiteSpace: h6Styles.whiteSpace,
            overflow: h6Styles.overflow,
            textOverflow: h6Styles.textOverflow,
          }}
        >
          {locationText}
        </Typography>
      </Stack>
      <ScrollableResultsStack spacing={isInlayMap ? 0.5 : 1.5} width={1} isLoading={isLoading}>
        {!searchResults.addresses || searchResults.addresses?.result.results.length === 0
          ? searchQuery && searchQuery.length < API_CALL_TEXT_LENGTH
            ? <Typography
              noWrap
              color="text.secondary"
              p={1}
              whiteSpace={isInlayMap ? 'unset' : 'nowrap'}
              textOverflow={isInlayMap ? 'unset' : 'ellipsis'}
            >
              {t('common:component.search.hint.less-than-count-map', { count: API_CALL_TEXT_LENGTH })}
            </Typography>
            : <Typography
              color="text.secondary"
              noWrap
              {...(mapSearchEnhancementFlag
                ? {
                  variant: 'caption',
                  fontStyle: 'italic',
                }
                : { p: 1 }
              )}
            >
              {noResultsInstructions}
            </Typography>
          : searchResults.addresses?.result.results.map((item, index) => (
            <StyledPaper
              key={index}
              data-testid={testid`thing-map-search-location-${index}`}
              elevation={(index + thingsLength + geofenceLength) === highlightIndex ? 3 : 0}
              onClick={() => {
                const address = item as unknown as MapDiscoverItem;
                onAddressClick?.(item);
                if (!isInlayMap) {
                  onAddressSelected(address, map);
                  setSearchPosition(address.position);
                }
              }}
            >
              {handleFormatAddressItem(item)}
            </StyledPaper>
          ))
        }
      </ScrollableResultsStack>
      {searchPosition && <LocationPin position={searchPosition} />}
    </Stack>
  </>;
}
